import React, { useState } from 'react';
import { useMutation, useQuery } from 'react-query';
import AxiosUtil from '../../Utils/AxiosUtil';
import AIContextAssistModal from '../Shared/AIContextAssistModal';
import { buildReportPageLayoutRulesText, extractAIReportPageLayoutSuggestionText } from '../Shared/AIReportPageLayoutAssistRules';
import GetAIProviderListModelRequest from '../../Interfaces/Requests/AI/GetAIProviderListModelRequest';
import GetAISingleTurnPromptModelRequest from '../../Interfaces/Requests/AI/GetAISingleTurnPromptModelRequest';
import GetAIProviderListModelResponse from '../../Interfaces/Responses/AI/GetAIProviderListModelResponse';
import GetAISingleTurnPromptModelResponse from '../../Interfaces/Responses/AI/GetAISingleTurnPromptModelResponse';
import { ManagedReportPage, ManagedReportItem } from '../Shared/ManagedReport';
import {
    ManagedFormRule,
    ManagedFormErrorInfo,
    ManagedAdminEditorMode,
    ManagedAdminActionMode,
    ManagedAdminChildOperation,
    ManagedActionModal,
    ManagedActionModalState,
    buildManagedActionModalState,
 } from '../Shared/ManagedHelpers';
import {
    AddIcon,
    Button,
    Checkbox,
    Col,
    DeleteIcon,
    Divider,
    EditIcon,
    Form,
    Input,
    Modal as ServaModal,
    Option,
    Row,
    Select,
    Space,
    Spin,
    Text,
    Title,
} from '@servahealth/serva-ui-kit';
import TextArea from 'antd/es/input/TextArea';
import { CloseCircleOutlined } from '@ant-design/icons';
import BulbOutlinedIlluminated from '../Shared/BulbOutlinedIlluminated';
import AUDReportItems, { ManagedReportItemEditResult } from './AUDReportItems';

export type ManagedReportPageEditResult = {
    operation: ManagedAdminChildOperation;
    reportPage: ManagedReportPage;
};

type AUDReportPageFormValues = {
    rpPageNum: number;
    rpPageTitle: string;
    rpLayout: string;
    rpPDFLayout: string;
    rpHide: boolean;
};

type AUDReportPagesProps = {
    reportID: number;
    reportName: string;
    reportGroupName: string;
    reportPages: ManagedReportPage[];
    initialSelectedReportPageID?: number;
    initialMode?: ManagedAdminActionMode;
    handleSetReportPages: (
	    result: ManagedReportPageEditResult
	) => void;
    close: () => void;
};

const AUDReportPages: React.FC<AUDReportPagesProps> = ({ close, ...props }) => {
    const [formAUDReportPage] = Form.useForm();

    const [aiLayoutAssistModalOpen, setAILayoutAssistModalOpen] = useState(false);
    const [aiLayoutAssistCurrentText, setAILayoutAssistCurrentText] = useState('');
    const [aiLayoutAssistResultText, setAILayoutAssistResultText] = useState('');
    const [aiLayoutAssistAncillaryItems, setAILayoutAssistAncillaryItems] = useState<string[]>([]);
    const [aiLayoutAssistLoading, setAILayoutAssistLoading] = useState(false);
    const [aiLayoutAssistContext, setAILayoutAssistContext] = useState({
        title: 'AI Report Page Layout Assist',
        currentSectionTitle: 'Current Layout',
        requestSectionTitle: 'What do you want this layout assist to do?',
        resultSectionTitle: 'AI Layout Assist Result',
        requestPlaceholder: 'Ask AI to explain, review, correct, or create Report Page Layout HTML.',
        askButtonText: 'Ask AI',
        useButtonText: 'Use Layout',
        allowUseSuggestion: true,
    });

    const reportPages = props.reportPages ?? [];

    const getInitialRPMode = (): ManagedAdminActionMode => {
        switch (props.initialMode) {
            case 'update':
                return 'update';
            case 'delete':
                return 'delete';
            case 'add':
            default:
                return 'add';
        }
    };

    const getInitialSelectedReportPage = (): ManagedReportPage | undefined => {
        if (!props.initialSelectedReportPageID || props.initialSelectedReportPageID <= 0) {
            return undefined;
        }

        return reportPages.find(
            (reportPage) => reportPage.id === props.initialSelectedReportPageID
        );
    };

    const initialSelectedReportPage = getInitialSelectedReportPage();
    const initialRPMode = getInitialRPMode();

    const initialRPFormValues = initialRPMode === 'add'
        ? {
            rpPageNum: 1,
            rpPageTitle: '',
            rpLayout: '',
            rpPDFLayout: '',
            rpHide: false,
        }
        : {
            rpPageNum: initialSelectedReportPage?.pageNum ?? 1,
            rpPageTitle: initialSelectedReportPage?.pageTitle ?? '',
            rpLayout: initialSelectedReportPage?.layout ?? '',
            rpPDFLayout: initialSelectedReportPage?.pdfLayout ?? '',
            rpHide: initialSelectedReportPage?.hide ?? false,
        };

    const [addButtonClicked, setAddButtonClicked] = useState(initialRPMode === 'add');
    const [updButtonClicked, setUpdButtonClicked] = useState(initialRPMode === 'update');
    const [delButtonClicked, setDelButtonClicked] = useState(initialRPMode === 'delete');

    const [rpSelectedReportPageID, setRPSelectedReportPageID] = useState(initialSelectedReportPage?.id ?? -1);
    const [rpSelectedReportPageTitle, setRPSelectedReportPageTitle] = useState(
        initialSelectedReportPage?.pageTitle ?? ''
    );

    const rpPropsReportID = props.reportID > 0 ? props.reportID : 0;
    const rpPropsReportName = props.reportName !== '' ? props.reportName : 'New Report';
    const rpPropsReportGroupName = props.reportGroupName !== '' ? props.reportGroupName : 'New Report Group';

    const [rpDetailsReportItems, setRPDetailsReportItems] = useState<ManagedReportItem[]>(
        initialSelectedReportPage?.reportItems ?? []
    );

    const [manageReportItemsModalOpen, setManageReportItemsModalOpen] = useState(false);

    const [managedActionModalState, setManagedActionModalState] = useState<ManagedActionModalState>({
        open: false,
        mode: 'info',
        title: '',
        message: '',
        isError: false,
    });

    const { data: aiProviders = [], isLoading: isGetAIProvidersLoading } = useQuery<GetAIProviderListModelResponse[], unknown>(
        ['aud-report-page-layout-ai-providers'],
        async () => {
            try {
                const url = `${process.env.REACT_APP_API_ROOT}/api/AI/httpGet_GetAIProviders`;
                const requestData: GetAIProviderListModelRequest = {};

                return await AxiosUtil.get<GetAIProviderListModelResponse[], GetAIProviderListModelRequest>(url, requestData);
            } catch (err) {
                console.error('AUD_REPORT_PAGE_LAYOUT_AI_PROVIDERS: [Get AI Providers] Internal Error: ', err);
                return Promise.reject(err);
            }
        },
        {
            onError: (err: unknown) => {
                console.error('AUD_REPORT_PAGE_LAYOUT_AI_PROVIDERS: [Get AI Providers] Internal Error: ', err);
                setManagedActionModalState(
                    buildManagedActionModalState(
                        'info',
                        'AI Provider Load Error',
                        'Unable to load AI Providers for Report Page Layout assistance.',
                        true
                    )
                );
            },
        }
    );

    const getAILayoutAssistProviderNamePhrase = (): string => {
        return 'Report Page Layout Assist';
    };

    const getAILayoutAssistProvider = (): GetAIProviderListModelResponse | undefined => {
        const providerNamePhrase = getAILayoutAssistProviderNamePhrase().toLowerCase();

        return aiProviders.find((provider) =>
            provider.name.toLowerCase().includes(providerNamePhrase) ||
            provider.displayName.toLowerCase().includes(providerNamePhrase)
        );
    };

    const buildReportItemSummaryForAILayoutAssist = (): string => {
        if (rpDetailsReportItems.length === 0) {
            return '(No Report Items are currently linked to this Report Page.)';
        }

        return rpDetailsReportItems
            .map((reportItem) => {
                const itemType = reportItem.chartId && reportItem.chartId > 0
                    ? 'Chart'
                    : 'DataSet';

                return [
                    `Report Item ID: ${reportItem.id}`,
                    `Placeholder: {${reportItem.id}}`,
                    `Type: ${itemType}`,
                    `DataSet ID: ${reportItem.dataSetId ?? '(none)'}`,
                    `Chart ID: ${reportItem.chartId ?? '(none)'}`,
                    `Hide Title: ${reportItem.hideTitle ? 'Yes' : 'No'}`,
                ].join('; ');
            })
            .join('\n');
    };

    const buildAILayoutAssistPrompt = (requestText: string): string => {
        const currentLayoutText = formAUDReportPage.getFieldValue('rpLayout') ?? '';

        return [
            'RetainBI Report Page Layout assistance request.',
            '',
            'Report context:',
            `Report ID: ${rpPropsReportID}`,
            `Report Name: ${rpPropsReportName}`,
            `Report Group: ${rpPropsReportGroupName}`,
            `Report Page ID: ${formatRPDisplayID(rpSelectedReportPageID)}`,
            `Report Page Title: ${formAUDReportPage.getFieldValue('rpPageTitle') ?? ''}`,
            '',
            'Available Report Items:',
            buildReportItemSummaryForAILayoutAssist(),
            '',
            'Current Report Page Layout HTML:',
            currentLayoutText,
            '',
            'Report Page Layout Rules:',
            buildReportPageLayoutRulesText(),
            '',
            'User request:',
            requestText,
            '',
            'Output instructions:',
            'If the user asks to explain or review the layout, return a concise explanation or review.',
            'If the user asks to create, correct, rewrite, or replace the layout, return only drop-in ready Report Page Layout HTML.',
            'Do not include credentials, connection strings, secrets, PHI, PII, tenant-isolated data, or system security details.',
            'Do not suggest executing SQL or changing data. The user must review and apply any suggestion manually.',
        ].join('\n');
    };

    const { mutate: getAILayoutAssistPrompt } = useMutation(
        async ({ externalVendorID, tenantID, prompt }: GetAISingleTurnPromptModelRequest) => {
            try {
                const url = `${process.env.REACT_APP_API_ROOT}/api/AI/httpPost_AISingleTurnPrompt`;
                const requestData: GetAISingleTurnPromptModelRequest = {
                    externalVendorID: externalVendorID,
                    tenantID: tenantID,
                    prompt: prompt,
                };

                return await AxiosUtil.post<GetAISingleTurnPromptModelResponse, GetAISingleTurnPromptModelRequest>(url, requestData);
            } catch (err) {
                console.error('AUD_REPORT_PAGE_LAYOUT_AI_ASSIST: [AI Layout Assist] Internal Error: ', err);
                return Promise.reject(err);
            }
        },
        {
            onSuccess: (response: GetAISingleTurnPromptModelResponse) => {
                const rawResponseText = response.text ?? '';
                const suggestedLayoutText = extractAIReportPageLayoutSuggestionText(rawResponseText);
                const responseWasCleaned = rawResponseText.trim() !== suggestedLayoutText.trim();

                setAILayoutAssistResultText(suggestedLayoutText);
                setAILayoutAssistAncillaryItems([
                    response.success ? 'AI layout assist returned successfully.' : 'AI layout assist request did not succeed.',
                    responseWasCleaned ? 'RetainBI removed markdown formatting before showing the layout assist result.' : '',
                    response.message ?? '',
                ].filter((item) => item.trim().length > 0));
                setAILayoutAssistLoading(false);
            },
            onError: (err: unknown) => {
                console.error('AUD_REPORT_PAGE_LAYOUT_AI_ASSIST: [AI Layout Assist] Internal Error: ', err);
                setAILayoutAssistAncillaryItems([
                    'Unable to complete the AI Report Page Layout assistance request.',
                ]);
                setAILayoutAssistLoading(false);
            },
        }
    );

    const handleOpenAILayoutAssistModal = () => {
        setAILayoutAssistContext({
            title: 'AI Report Page Layout Assist',
            currentSectionTitle: 'Current Layout',
            requestSectionTitle: 'What do you want this layout assist to do?',
            resultSectionTitle: 'AI Layout Assist Result',
            requestPlaceholder: 'Ask AI to explain, review, correct, or create Report Page Layout HTML.',
            askButtonText: 'Ask AI',
            useButtonText: 'Use Layout',
            allowUseSuggestion: true,
        });
        setAILayoutAssistCurrentText(formAUDReportPage.getFieldValue('rpLayout') ?? '');
        setAILayoutAssistResultText('');
        setAILayoutAssistAncillaryItems([]);
        setAILayoutAssistModalOpen(true);
    };

    const handleAskAILayoutAssist = (requestText: string) => {
        const aiLayoutAssistProvider = getAILayoutAssistProvider();

        if (!aiLayoutAssistProvider) {
            setAILayoutAssistAncillaryItems([
                `Unable to find an AI Provider matching ${getAILayoutAssistProviderNamePhrase()}.`,
            ]);
            return;
        }

        setAILayoutAssistLoading(true);
        setAILayoutAssistResultText('');
        setAILayoutAssistAncillaryItems([]);

        getAILayoutAssistPrompt({
            externalVendorID: aiLayoutAssistProvider.externalVendorID,
            tenantID: null,
            prompt: buildAILayoutAssistPrompt(requestText),
        });
    };

    const handleCopyAILayoutAssistResult = (resultText: string) => {
        navigator.clipboard.writeText(resultText);
    };

    const handleUseAILayoutAssistSuggestion = (resultText: string) => {
        formAUDReportPage.setFieldValue('rpLayout', resultText);
        setAILayoutAssistModalOpen(false);
    };

    const setRPMode = (mode: ManagedAdminActionMode) => {
        setAddButtonClicked(mode === 'add');
        setUpdButtonClicked(mode === 'update');
        setDelButtonClicked(mode === 'delete');
    };

    const getRPOperation = (): ManagedAdminChildOperation => {
        if (addButtonClicked) return 'add';
        if (updButtonClicked) return 'update';
        return 'delete';
    };

    const getRPActionColor = (): string => {
        if (addButtonClicked) return 'cornflowerblue';
        if (updButtonClicked) return 'darkgray';
        if (delButtonClicked) return 'red';
        return 'black';
    };

    const clearRPSelection = () => {
        setRPSelectedReportPageID(-1);
        setRPSelectedReportPageTitle('');
        setRPDetailsReportItems([]);
        setRPMode('add');
        setRPBlankForm();
    };

    const getSelectedReportPage = (
        selectedReportPageID: number = rpSelectedReportPageID
    ): ManagedReportPage | undefined => {
        return reportPages.find((reportPage) => reportPage.id === selectedReportPageID);
    };

    const formatRPDisplayID = (id?: number | null): string => {
        return id && id > 0 ? String(id) : 'new';
    };

    const buildReportPageNameConstruct = (reportPage: ManagedReportPage | undefined): string => {
        if (!reportPage) {
            return '';
        }

        const pageTitle = reportPage.pageTitle && reportPage.pageTitle.trim() !== ''
            ? reportPage.pageTitle
            : '{No Page Title}';

        return 'Page ' + reportPage.pageNum + ': ' + pageTitle;
    };

    const setRPBlankForm = () => {
        formAUDReportPage.setFieldValue('rpPageNum', 1);
        formAUDReportPage.setFieldValue('rpPageTitle', '');
        formAUDReportPage.setFieldValue('rpLayout', '');
        formAUDReportPage.setFieldValue('rpPDFLayout', '');
        formAUDReportPage.setFieldValue('rpHide', false);

        setRPDetailsReportItems([]);
    };

    const setRPFormFromSelectedReportPage = () => {
        const selectedReportPage = getSelectedReportPage();

        formAUDReportPage.setFieldValue('rpPageNum', selectedReportPage?.pageNum ?? 1);
        formAUDReportPage.setFieldValue('rpPageTitle', selectedReportPage?.pageTitle ?? '');
        formAUDReportPage.setFieldValue('rpLayout', selectedReportPage?.layout ?? '');
        formAUDReportPage.setFieldValue('rpPDFLayout', selectedReportPage?.pdfLayout ?? '');
        formAUDReportPage.setFieldValue('rpHide', selectedReportPage?.hide ?? false);

        setRPDetailsReportItems(selectedReportPage?.reportItems ?? []);
    };

    const handleRPReportPageResetForm = (mode: ManagedAdminEditorMode) => {
        if (mode === 'blank') {
            setRPBlankForm();
            return;
        }

        setRPFormFromSelectedReportPage();
    };

    const isBlank = (value: unknown): boolean => {
        return value === undefined || value === null || String(value).trim().length === 0;
    };

    const validateRequiredRPField = (fieldName: string): Promise<void> => {
        const value = formAUDReportPage.getFieldValue(fieldName);
        return isBlank(value) ? Promise.reject() : Promise.resolve();
    };

    const validateRPDetailsData = (rule: ManagedFormRule, value: string) => {
        switch (rule.field) {
            case 'rpPageNum':
            case 'rpPageTitle':
            case 'rpLayout':
                return validateRequiredRPField(rule.field);

            case 'rpPDFLayout':
            case 'rpHide':
                return Promise.resolve();

            default:
                return Promise.reject();
        }
    };

    const closeRPReportItemsEditor = () => {
        setManageReportItemsModalOpen(false);
    };

    const onRPItems = () => {
        setManageReportItemsModalOpen(true);
    }

    const buildManagedReportPageRequest = (values: AUDReportPageFormValues): ManagedReportPage => {
        return {
            id: addButtonClicked ? 0 : (updButtonClicked ? rpSelectedReportPageID : (delButtonClicked ? rpSelectedReportPageID : 0)),
            reportId: rpPropsReportID,
            pageNum: values.rpPageNum,
            pageTitle: values.rpPageTitle,
            layout: values.rpLayout,
            pdfLayout: values.rpPDFLayout,
            hide: values.rpHide,
            reportItems: rpDetailsReportItems,
        };
    };

    const handleCloseManagedActionModal = () => {
        setManagedActionModalState((currentState) => ({
            ...currentState,
            open: false,
        }));
    };

    const onFinishFailed = (errorInfo: ManagedFormErrorInfo<AUDReportPageFormValues>) => {
        console.error('AUDReportPages validation failed:', errorInfo);

        setManagedActionModalState(
            buildManagedActionModalState(
                'info',
                'Report Page Validation',
                'Please correct the highlighted Report Page fields and try again.',
                true
            )
        );
    };

    const onFinish = (values: AUDReportPageFormValues) => {
        const newManagedReportPage = buildManagedReportPageRequest(values);

        props.handleSetReportPages({
            operation: getRPOperation(),
            reportPage: newManagedReportPage,
        });

        close();
    };

    const handleRPSelectionChange = (value: number) => {
        const selectedReportPage = getSelectedReportPage(value);

        setRPSelectedReportPageID(value);
        setRPSelectedReportPageTitle(buildReportPageNameConstruct(selectedReportPage));

        setRPMode('add');
        handleRPReportPageResetForm('blank');
    };

    //=================================================================
    //=================================================================
    
    const handleRPAddReportPage = () => {
        clearRPSelection();
    };

    //=================================================================
    //=================================================================

    const handleRPUpdReportPage = () => {
        setRPMode('update');
        handleRPReportPageResetForm('selected');
    };

    //=================================================================
    //=================================================================

    const handleRPDelReportPage = () => {
        setRPMode('delete');
        handleRPReportPageResetForm('selected');
    };

    //=================================================================
    //=================================================================

    const onRPCancel = () => {
        clearRPSelection();
    };

    const contentStyle: React.CSSProperties = {
        lineHeight: '95%',
        textAlign: 'center',
        color: 'black',
        backgroundColor: 'azure',
        borderRadius: '10px',
        border: `1px solid black`,
        marginTop: '16px',
        paddingRight: '8px',
        paddingLeft: '8px',
        height: '375px'
    };
    
    const handleSetReportItems = (result: ManagedReportItemEditResult) => {
        setRPDetailsReportItems((currentReportItems) => {
            switch (result.operation) {
                case 'add':
                    return [
                        ...currentReportItems,
                        result.reportItem,
                    ];

                case 'update':
                    return currentReportItems.map((currentReportItem) =>
                        currentReportItem.id === result.reportItem.id
                            ? result.reportItem
                            : currentReportItem
                    );

                case 'delete':
                    return currentReportItems.filter(
                        (currentReportItem) => currentReportItem.id !== result.reportItem.id
                    );

                default:
                    return currentReportItems;
            }
        });
    };

    return (
        <>
            <ManagedActionModal
                modalState={managedActionModalState}
                onOk={handleCloseManagedActionModal}
            />
            <AIContextAssistModal
                open={aiLayoutAssistModalOpen}
                title={aiLayoutAssistContext.title}
                currentSectionTitle={aiLayoutAssistContext.currentSectionTitle}
                currentText={aiLayoutAssistCurrentText}
                requestSectionTitle={aiLayoutAssistContext.requestSectionTitle}
                requestPlaceholder={aiLayoutAssistContext.requestPlaceholder}
                resultSectionTitle={aiLayoutAssistContext.resultSectionTitle}
                resultText={aiLayoutAssistResultText}
                ancillaryItems={aiLayoutAssistAncillaryItems}
                ancillarySectionTitle='Ancillary Information'
                loading={aiLayoutAssistLoading}
                askButtonText={aiLayoutAssistContext.askButtonText}
                useButtonText={aiLayoutAssistContext.useButtonText}
                allowUseSuggestion={aiLayoutAssistContext.allowUseSuggestion}
                onAskAI={handleAskAILayoutAssist}
                onCopy={handleCopyAILayoutAssistResult}
                onUseSuggestion={handleUseAILayoutAssistSuggestion}
                onClose={() => setAILayoutAssistModalOpen(false)}
            />
            <ServaModal
                closeIcon={<CloseCircleOutlined></CloseCircleOutlined>}
                destroyOnHidden={true}
                hideButtons={true}
                width='60%'
                open={manageReportItemsModalOpen}
                onCancel={closeRPReportItemsEditor}
            >
                <AUDReportItems
                    reportID={rpPropsReportID}
                    reportName={rpPropsReportName}
                    reportGroupName={rpPropsReportGroupName}
                    reportPageID={rpSelectedReportPageID}
                    reportPageTitle={rpSelectedReportPageTitle}
                    reportItems={rpDetailsReportItems}
                    handleSetReportItems={handleSetReportItems}
                    close={closeRPReportItemsEditor}
                ></AUDReportItems>
            </ServaModal>
            <Spin spinning={false}>
                <Space direction='horizontal' style={{justifyContent: 'center', padding: '4px', width: '100%'}}>
                    <Text style={{height: '100%', width: '100%'}}>
                        <Title style={{backgroundColor: 'rgb(00, 55, 90)', borderRadius: '10px', color: 'white', height: '100%', padding: '8px', width: '100%'}} level={4}>Manage Report Pages</Title>
                    </Text>
                </Space>
                <Space direction='horizontal' style={{justifyContent: 'center', padding: '2px', width: '100%'}}>
                    <Text italic type='secondary'>{'Report: [' + formatRPDisplayID(rpPropsReportID) + '] ' + rpPropsReportGroupName + '-' + rpPropsReportName}</Text>
                </Space>
                <Space direction='horizontal' style={{justifyContent: 'center', padding: '2px', width: '100%'}}>
                    <Text italic type='secondary'>{'Report Page: [' + formatRPDisplayID(rpSelectedReportPageID) + '] ' + rpSelectedReportPageTitle}</Text>
                </Space>
                <Row gutter={16} className='retainbi-compact-row'>
                    <Col span={1} />
                    <Col span={22}>
                        <div>
                            <Space direction='horizontal' style={{width: '100%', justifyContent: 'left'}}>
                                <Text italic>Select an action below to proceed.</Text>
                            </Space>
                            <Divider style={{ borderTop: '1px solid black', margin: '1px' }}></Divider>
                        </div>
                    </Col>
                    <Col span={1} />
                </Row>
                <Row gutter={16} className='retainbi-compact-row'>
                    <Col span={2} />
                    <Col span={8}>
                        <Text italic style={{float: 'right'}}>Select Report Page to Update or Delete:</Text>
                    </Col>
                    <Col span={14}>
                        <Select
                            styles={{ popup: { root: { width: 500 } } }}
                            filterOption={(input, option) => (option?.label as string ?? '').toLowerCase().includes(input.toLowerCase())}
                            placeholder={'Select a Report Page'}
                            showSearch
                            style={{paddingTop: '4px', width: '95%'}}
                            value={rpSelectedReportPageID > 0 ? rpSelectedReportPageID : undefined}
                            onChange={handleRPSelectionChange}
                        >
                            {reportPages.map(
                                (eachReportPage) => {
                                    return (
                                        <Option
                                            key={eachReportPage.id}
                                            label={buildReportPageNameConstruct(eachReportPage)}
                                            value={eachReportPage.id}
                                        >
                                            {buildReportPageNameConstruct(eachReportPage)}
                                        </Option>
                                    );
                                }
                            )}
                        </Select>
                    </Col>
                </Row>
                <Row gutter={16} className='retainbi-compact-row'>
                    <Col span={5}>
                        <Button
                            icon={<AddIcon />}
                            style={{ float: 'right' , marginBottom: '4px', width: '100%' }}
                            type='primary'
                            onClick={handleRPAddReportPage}
                        >
                            Add Report Page
                        </Button>
                    </Col>
                    <Col span={8} />
                    <Col span={5}>
                        <Button
                            disabled={rpSelectedReportPageID === -1}
                            icon={<EditIcon />}
                            style={{ backgroundColor: 'darkgray', float: 'right', marginBottom: '4px', width: '100%' }}
                            type='primary'
                            onClick={handleRPUpdReportPage}
                        >
                            Update Report Page
                        </Button>
                    </Col>
                    <Col span={1} />
                    <Col span={5}>
                        <Button
                            disabled={rpSelectedReportPageID === -1}
                            icon={<DeleteIcon />}
                            style={{ backgroundColor: 'red', float: 'right', marginBottom: '4px', width: '100%' }}
                            type='primary'
                            onClick={handleRPDelReportPage}
                        >
                            Delete Report Page
                        </Button>
                    </Col>
                </Row>
                <Row gutter={16} className='retainbi-compact-row'>
                    <Col span={24}>
                        <div>
                            <Divider style={{ borderTop: '1px solid black', margin: '1px' }}></Divider>
                        </div>
                    </Col>
                </Row>
                <Form
                    form={formAUDReportPage}
                    initialValues={initialRPFormValues}
                    onFinish={onFinish}
                    onFinishFailed={onFinishFailed}
                >
                    <div style={contentStyle}>
                        <Row gutter={16} className='retainbi-compact-row'>
                            <Col span={21}>
                                <Divider orientation='left' orientationMargin={30} variant='dotted' style={{ borderColor: 'black', color: getRPActionColor() }}>
                                    {addButtonClicked ? 'Add Report Page' :
                                        (updButtonClicked ? 'Update Report Page: [' + formatRPDisplayID(rpSelectedReportPageID) + '] ' :
                                            (delButtonClicked ? 'Delete Report Page: [' + formatRPDisplayID(rpSelectedReportPageID) + '] ' : ''))}
                                </Divider>
                            </Col>
                            <Col span={3}>
                                <Button
                                    disabled={delButtonClicked}
                                    style={{ color: getRPActionColor(), fontStyle: 'italic' }}
                                    type='link'
                                    onClick={() => handleRPReportPageResetForm(addButtonClicked ? 'blank' : 'selected')}
                                >
                                    Reset Form
                                </Button>
                            </Col>
                        </Row>
                        <Row gutter={16} className='retainbi-compact-row'>
                            <Col span={8}>
                                <div style={{textAlign:'left'}}>
                                    <Form.Item
                                        name='rpPageNum'
                                        label='Page Number'
                                        labelCol={{ span: 10, }}
                                        rules={[
                                            {
                                                validator: validateRPDetailsData,
                                            },
                                        ]}
                                        style={{ marginBottom: '0px' }}
                                    >
                                        <Input
                                            disabled={delButtonClicked}
                                            placeholder='Enter Page Number'
                                            style={{color: delButtonClicked ? 'gray' : 'black', float: 'left', width: '75%'}}
                                            type='number'
                                        />
                                    </Form.Item>
                                </div>
                            </Col>
                            <Col span={10}>
                                <div style={{textAlign:'left'}}>
                                    <Form.Item
                                        name='rpPageTitle'
                                        label='Page Title'
                                        labelCol={{ span: 5, }}
                                        rules={[
                                            {
                                                validator: validateRPDetailsData,
                                            },
                                        ]}
                                        style={{ marginBottom: '0px' }}
                                    >
                                        <Input
                                            disabled={delButtonClicked}
                                            placeholder='Enter Page Title'
                                            style={{color: delButtonClicked ? 'gray' : 'black', float: 'left', width: '90%'}}
                                        />
                                    </Form.Item>
                                </div>
                            </Col>
                            <Col span={6}>
                                <Form.Item
                                    key='rphide'
                                    name='rpHide'
                                    valuePropName='checked'
                                    style={{alignItems:'center', marginBottom: '0px', marginTop: '0px'}}
                                >
                                    <Checkbox
                                        defaultChecked={true}
                                        disabled={delButtonClicked}
                                    >
                                        Hide
                                    </Checkbox>
                                </Form.Item>
                            </Col>
                        </Row>
                        <Row gutter={16} className='retainbi-compact-row'>
                            <Col span={24}>
                                <Form.Item
                                    label='Layout'
                                    labelCol={{ span: 2, }}
                                    style={{marginBottom: '8px'}}
                                >
                                    <div style={{ display: 'flex', gap: '4px', width: '100%' }}>
                                        <Form.Item
                                            name='rpLayout'
                                            rules={[
                                                {
                                                    message: 'Layout required.',
                                                    required: true,
                                                    validator: validateRPDetailsData,
                                                },
                                            ]}
                                            style={{ flex: 1, marginBottom: '0px' }}
                                        >
                                            <TextArea
                                                disabled={delButtonClicked}
                                                placeholder='Enter Layout'
                                                rows={3}
                                                style={{color: delButtonClicked ? 'gray' : 'black', resize: 'none'}}
                                            ></TextArea>
                                        </Form.Item>
                                        <Button
                                            disabled={delButtonClicked || isGetAIProvidersLoading}
                                            icon={<BulbOutlinedIlluminated />}
                                            style={{ minWidth: '32px', padding: '0px', }}
                                            title='AI Assist'
                                            type='default'
                                            onClick={handleOpenAILayoutAssistModal}
                                        />
                                    </div>
                                </Form.Item>
                            </Col>
                        </Row>
                        <Row gutter={16} className='retainbi-compact-row'>
                            <Col span={24}>
                                <Form.Item
                                    label='PDF Layout'
                                    labelCol={{ span: 2, }}
                                    style={{marginBottom: '8px'}}
                                >
                                    <div style={{ display: 'flex', gap: '4px', width: '100%' }}>
                                        <Form.Item
                                            name='rpPDFLayout'
                                            rules={[
                                                {
                                                    validator: validateRPDetailsData,
                                                },
                                            ]}
                                            style={{ flex: 1, marginBottom: '0px' }}
                                        >
                                            <TextArea
                                                disabled={delButtonClicked}
                                                placeholder='Enter PDF Layout'
                                                rows={3}
                                                style={{color: delButtonClicked ? 'gray' : 'black', resize: 'none'}}
                                            ></TextArea>
                                        </Form.Item>
                                        <div style={{ minWidth: '32px' }} />
                                    </div>
                                </Form.Item>
                            </Col>
                        </Row>
                        <Row gutter={16} className='retainbi-compact-row'>
                            <Col span={1} />
                            <Col span={22}>
                                <div>
                                    <Divider style={{ borderTop: '1px solid black', margin: '1px' }}></Divider>
                                </div>
                            </Col>
                            <Col span={1} />
                        </Row>
                        <Row gutter={16} className='retainbi-compact-row'>
                            <Col span={4}>
                                <Form.Item
                                    key='rpreportitems'
                                    name='rpReportItems'
                                    style={{margin: '4px'}}
                                >
                                    <Button
                                        style={{ width: '95%', backgroundColor: addButtonClicked ? 'cornflowerblue' : (updButtonClicked ? 'darkgray' : (delButtonClicked ? 'red' : 'black')) }}
                                        type='primary'
                                        onClick={onRPItems}
                                    >
                                        Report Items
                                    </Button>
                                </Form.Item>
                            </Col>
                            <Col span={20} />
                        </Row>
                        <Row gutter={16} className='retainbi-compact-row'>
                            <Col span={1} />
                            <Col span={22}>
                                <div>
                                    <Divider style={{ borderTop: '1px solid black', margin: '1px' }}></Divider>
                                </div>
                            </Col>
                            <Col span={1} />
                        </Row>
                    </div>
                    <Row gutter={16} className='retainbi-compact-row' style={{ paddingTop: '8px' }}>
                        <Col span={5}>
                            <Form.Item
                                name='rpCloseButton'
                                style={{ marginBottom: '0px' }}
                            >
                                <Button
                                    style={{ float: 'right', width: '95%' }}
                                    type='default'
                                    onClick={close}
                                >
                                    Close
                                </Button>
                            </Form.Item>
                        </Col>
                        <Col span={9} />
                        <Col span={5}>
                            <Form.Item
                                name='rpApplyButton'
                                style={{ marginBottom: '0px' }}
                            >
                                <Button
                                    htmlType='submit'
                                    style={{ backgroundColor: addButtonClicked ? 'cornflowerblue' : (updButtonClicked ? 'darkgray' : (delButtonClicked ? 'red' : 'black')), float: 'right', width: '95%' }}
                                    type='primary'
                                >
                                    {addButtonClicked ? 'Add Report Page' : (updButtonClicked ? 'Update Report Page' : (delButtonClicked ? 'Delete Report Page' : ''))}
                                </Button>
                            </Form.Item>
                        </Col>
                        <Col span={5}>
                            <Form.Item
                                name='rpCancelButton'
                                style={{ marginBottom: '0px' }}
                            >
                                <Button
                                    style={{ float: 'right', width: '95%' }}
                                    type='default'
                                    onClick={onRPCancel}
                                >
                                    {addButtonClicked ? 'Cancel Add' : (updButtonClicked ? 'Cancel Update' : (delButtonClicked ? 'Cancel Delete' : ''))}
                                </Button>
                            </Form.Item>
                        </Col>
                    </Row>
                </Form>
            </Spin>
        </>
    );
};

export default AUDReportPages;
